import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Flame, LogOut, ArrowLeft, Pizza, ClipboardList } from "lucide-react";
import api, { setAdminToken, formatApiError } from "@/lib/api";
import PizzaManager from "@/components/admin/PizzaManager";
import OrderManager from "@/components/admin/OrderManager";

export default function Admin() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("pizzas");

  useEffect(() => {
    api.get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => setUser(null))
      .finally(() => setChecking(false));
  }, []);

  const login = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setAdminToken(data.token);
      setUser(data.user);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const logout = () => {
    setAdminToken(null);
    setUser(null);
  };

  if (checking) {
    return <div className="flex min-h-screen items-center justify-center text-dim">Vérification de la session…</div>;
  }

  if (!user) {
    return (
      <div className="flex min-h-screen items-center justify-center px-5">
        <div className="grain-overlay" />
        <motion.div
          data-testid="admin-login-modal"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-sm rounded-2xl border border-border bg-surface-2 p-8"
        >
          <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-terra flame-glow">
            <Flame size={20} className="text-[#FDFBF7]" />
          </span>
          <h1 className="font-display mt-5 text-center text-xl font-bold">Espace Admin</h1>
          <p className="mt-1 text-center text-sm text-dim">PALMI Pizza Club</p>
          <form onSubmit={login} className="mt-7 space-y-4">
            <input
              data-testid="admin-email-input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email"
              className="admin-input"
            />
            <input
              data-testid="admin-password-input"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Mot de passe"
              className="admin-input"
            />
            {error && <p data-testid="admin-login-error" className="text-sm text-flame">{error}</p>}
            <button data-testid="admin-login-submit-button" type="submit" disabled={busy} className="btn-primary w-full justify-center disabled:opacity-60">
              {busy ? "Connexion…" : "Se connecter"}
            </button>
          </form>
          <Link to="/" className="mt-6 flex items-center justify-center gap-1.5 text-xs text-dim hover:text-terra transition-colors">
            <ArrowLeft size={13} /> Retour au site
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard-wrapper" className="min-h-screen">
      <header className="sticky top-0 z-50 border-b border-border/60 bg-background/80 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-5">
          <div className="flex items-center gap-3">
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-terra">
              <Flame size={15} className="text-[#FDFBF7]" />
            </span>
            <span className="font-display text-sm font-bold uppercase">Admin — PALMI Pizza Club</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden text-xs text-dim sm:block">{user.email}</span>
            <Link to="/" className="btn-ghost !px-4 !py-2 text-xs"><ArrowLeft size={13} /> Site</Link>
            <button data-testid="admin-logout-button" onClick={logout} className="btn-ghost !px-4 !py-2 text-xs hover:!text-flame">
              <LogOut size={13} /> Déconnexion
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-5 py-10">
        <div className="flex gap-2">
          <button
            data-testid="admin-tab-pizzas"
            onClick={() => setTab("pizzas")}
            className={`inline-flex items-center gap-2 rounded-full border px-5 py-2.5 text-sm font-medium transition-colors ${
              tab === "pizzas" ? "border-terra bg-terra text-[#FDFBF7]" : "border-border text-dim hover:border-terra"
            }`}
          >
            <Pizza size={15} /> La Carte
          </button>
          <button
            data-testid="admin-tab-orders"
            onClick={() => setTab("orders")}
            className={`inline-flex items-center gap-2 rounded-full border px-5 py-2.5 text-sm font-medium transition-colors ${
              tab === "orders" ? "border-terra bg-terra text-[#FDFBF7]" : "border-border text-dim hover:border-terra"
            }`}
          >
            <ClipboardList size={15} /> Commandes
          </button>
        </div>
        <div className="mt-8">{tab === "pizzas" ? <PizzaManager /> : <OrderManager />}</div>
      </main>
    </div>
  );
}
